/**
 * Разбор Markdown в React-элементы.
 *
 * Своя реализация вместо библиотеки — по двум причинам.
 *
 * Первая: безопасность. Готовые рендереры отдают HTML-строку, которую
 * приходится вставлять через dangerouslySetInnerHTML, и тогда защита
 * от XSS держится на санитайзере. Здесь HTML-строки не возникает вовсе:
 * разбор сразу собирает React-элементы, а React экранирует текст сам.
 * Сырой HTML в исходнике статьи остаётся текстом, а не разметкой.
 *
 * Вторая: вес. Статьи базы знаний — инструкции и регламенты: заголовки,
 * списки, таблицы, ссылки, картинки, код. Полный CommonMark со сносками
 * и HTML-блоками здесь не нужен, а тянет в бандл водителя сотни килобайт.
 *
 * Поддерживается: заголовки, абзацы, списки (в том числе вложенные),
 * нумерованные списки, чек-листы `- [ ]`, цитаты, ограждённый код,
 * таблицы, горизонтальные линии, панели `:::info … :::`,
 * разворачиваемые блоки `:::expand Заголовок`, оглавление `[toc]`,
 * а внутри строки — жирный, курсив, зачёркнутый, код, ссылки
 * и изображения.
 *
 * Панели и разворот — синтаксис директив Docusaurus, чек-листы — GFM:
 * устоявшиеся расширения Markdown, а не собственная выдумка. Текст,
 * набранный здесь, останется читаемым в любом другом редакторе.
 */

const ESCAPABLE = '\\\\`*_{}[]()#+-.!|>~'

/** Разбить исходник на блоки. */
export function parseBlocks(src) {
  const lines = String(src || '').replace(/\r\n?/g, '\n').split('\n')
  const blocks = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (!line.trim()) {
      i += 1
      continue
    }

    // Директива: :::info Заголовок … ::: (панели и разворот).
    // Закрывается строкой из :::; незакрытая тянется до конца текста,
    // как и незакрытая ограда кода.
    const directive = line.match(/^\s*:::(info|note|warning|success|error|expand|left|center|right)\s*(.*)$/)
    if (directive) {
      const body = []
      let depth = 1
      i += 1
      while (i < lines.length) {
        if (/^\s*:::(info|note|warning|success|error|expand|left|center|right)\b/.test(lines[i])) depth += 1
        else if (/^\s*:::\s*$/.test(lines[i])) {
          depth -= 1
          if (depth === 0) break
        }
        body.push(lines[i])
        i += 1
      }
      i += 1 // закрывающая строка :::
      const inner = parseBlocks(body.join('\n'))
      if (directive[1] === 'expand') {
        blocks.push({ type: 'expand', title: directive[2].trim() || 'Подробнее', blocks: inner })
      } else if (['left', 'center', 'right'].includes(directive[1])) {
        blocks.push({ type: 'align', dir: directive[1], blocks: inner })
      } else {
        blocks.push({ type: 'panel', kind: directive[1], title: directive[2].trim(), blocks: inner })
      }
      continue
    }

    // Оглавление: [toc] отдельной строкой. Пункты собирает рендерер
    // из заголовков — парсер одного блока их ещё не видит целиком.
    if (/^\s*\[toc\]\s*$/i.test(line)) {
      blocks.push({ type: 'toc' })
      i += 1
      continue
    }

    // Ограждённый код: ```lang … ```
    const fence = line.match(/^\s*```(\w*)\s*$/)
    if (fence) {
      const body = []
      i += 1
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
        body.push(lines[i])
        i += 1
      }
      i += 1 // закрывающая ограда
      blocks.push({ type: 'code', lang: fence[1] || '', text: body.join('\n') })
      continue
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      blocks.push({
        type: 'heading',
        level: heading[1].length,
        text: heading[2].trim(),
      })
      i += 1
      continue
    }

    if (/^\s*([-*_])\s*\1\s*\1[\s\1]*$/.test(line)) {
      blocks.push({ type: 'hr' })
      i += 1
      continue
    }

    if (/^\s*>/.test(line)) {
      const body = []
      while (i < lines.length && /^\s*>/.test(lines[i])) {
        body.push(lines[i].replace(/^\s*>\s?/, ''))
        i += 1
      }
      blocks.push({ type: 'quote', blocks: parseBlocks(body.join('\n')) })
      continue
    }

    // Таблица: строка с | и следующая строка-разделитель.
    if (line.includes('|') && i + 1 < lines.length
        && /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(lines[i + 1])) {
      const header = splitRow(line)
      const align = splitRow(lines[i + 1]).map(alignOf)
      i += 2
      const rows = []
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitRow(lines[i]))
        i += 1
      }
      blocks.push({ type: 'table', header, align, rows })
      continue
    }

    const bullet = line.match(/^(\s*)([-*+]|\d+[.)])\s+/)
    if (bullet) {
      const ordered = /\d/.test(bullet[2])
      const items = []
      const baseIndent = bullet[1].length

      while (i < lines.length) {
        const m = lines[i].match(/^(\s*)([-*+]|\d+[.)])\s+(.*)$/)
        if (!m || m[1].length < baseIndent) break
        if (/\d/.test(m[2]) !== ordered && m[1].length === baseIndent) break

        if (m[1].length > baseIndent) {
          // Вложенный список — отдаём рекурсии, срезав отступ.
          const nested = []
          const indent = m[1].length
          while (i < lines.length && /^\s*([-*+]|\d+[.)])\s+/.test(lines[i])
                 && (lines[i].match(/^\s*/)[0].length >= indent)) {
            nested.push(lines[i].slice(baseIndent + 2))
            i += 1
          }
          const parsed = parseBlocks(nested.join('\n'))
          if (items.length) items[items.length - 1].blocks.push(...parsed)
          continue
        }

        const text = [m[3]]
        i += 1
        // Продолжение пункта на следующей строке с отступом.
        while (i < lines.length && lines[i].trim()
               && !/^\s*([-*+]|\d+[.)])\s+/.test(lines[i])
               && lines[i].match(/^\s*/)[0].length > baseIndent) {
          text.push(lines[i].trim())
          i += 1
        }
        // Чек-лист GFM: «- [ ] задача» / «- [x] сделана».
        const joined = text.join(' ')
        const task = joined.match(/^\[([ xX])\]\s+(.*)$/)
        if (task) {
          items.push({
            text: task[2],
            blocks: [],
            task: true,
            checked: task[1] !== ' ',
          })
        } else {
          items.push({ text: joined, blocks: [] })
        }
      }
      blocks.push({ type: 'list', ordered, items })
      continue
    }

    // Абзац: до пустой строки или начала другого блока.
    // Ведущие пробелы первой строки — отступ, два пробела на шаг,
    // как во вложенных списках. CommonMark сделал бы из четырёх
    // пробелов блок кода, но отступного кода здесь нет намеренно:
    // в статьях-регламентах отступ абзаца нужен чаще.
    const indent = Math.min(Math.floor(line.match(/^ */)[0].length / 2), 6)
    const para = []
    while (i < lines.length && lines[i].trim()
           && !/^\s*(#{1,6}\s|>|```|:::|\[toc\]\s*$)/i.test(lines[i])
           && !/^\s*([-*+]|\d+[.)])\s+/.test(lines[i])) {
      para.push(lines[i].trim())
      i += 1
    }
    if (para.length) {
      blocks.push({ type: 'paragraph', text: para.join(' '), indent })
    }
  }

  return blocks
}

/**
 * Переключить k-ю задачу чек-листа прямо в исходнике.
 *
 * Клик по квадратику в предпросмотре должен менять текст, который
 * потом сохранится, — иначе галочка живёт до первого ререндера.
 * Порядок задач в отрисованном документе совпадает с порядком строк
 * исходника (списки внутри панелей и цитат идут в исходном порядке),
 * поэтому достаточно найти k-е совпадение построчно.
 */
export function toggleTaskInSource(src, index) {
  const lines = String(src || '').replace(/\r\n?/g, '\n').split('\n')
  const re = /^(\s*(?:[-*+]|\d+[.)])\s+)\[([ xX])\](\s+.*)$/
  let k = -1
  return lines
    .map((l) => {
      const m = l.match(re)
      if (!m) return l
      k += 1
      if (k !== index) return l
      return m[1] + (m[2] === ' ' ? '[x]' : '[ ]') + m[3]
    })
    .join('\n')
}

function splitRow(line) {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|')
    .map((c) => c.trim())
}

function alignOf(spec) {
  const left = spec.startsWith(':')
  const right = spec.endsWith(':')
  if (left && right) return 'center'
  if (right) return 'right'
  return 'left'
}

/**
 * Разбор строки на инлайн-узлы.
 *
 * Возвращает массив узлов, а не строку: подстановка HTML исключена
 * по построению.
 */
export function parseInline(src) {
  const out = []
  let text = ''
  let i = 0
  const s = String(src || '')

  const flush = () => {
    if (text) {
      out.push({ type: 'text', value: text })
      text = ''
    }
  }

  // Парные маркеры проверяются от длинных к коротким: иначе `**` уйдёт
  // в курсив по первой звёздочке и жирный никогда не соберётся.
  const PAIRS = [
    ['***', 'strongEm'],
    ['**', 'strong'],
    ['__', 'strong'],
    ['~~', 'del'],
    ['*', 'em'],
    ['_', 'em'],
  ]

  outer: while (i < s.length) {
    const ch = s[i]

    if (ch === '\\' && ESCAPABLE.includes(s[i + 1])) {
      text += s[i + 1]
      i += 2
      continue
    }

    if (ch === '`') {
      const end = s.indexOf('`', i + 1)
      if (end > i) {
        flush()
        out.push({ type: 'code', value: s.slice(i + 1, end) })
        i = end + 1
        continue
      }
    }

    if (ch === '!' && s[i + 1] === '[') {
      const m = matchLink(s, i + 1)
      if (m) {
        flush()
        out.push({ type: 'image', alt: m.label, href: m.href })
        i = m.end
        continue
      }
    }

    if (ch === '[') {
      const m = matchLink(s, i)
      if (m) {
        flush()
        out.push({ type: 'link', href: m.href, children: parseInline(m.label) })
        i = m.end
        continue
      }
    }

    for (const [mark, type] of PAIRS) {
      if (!s.startsWith(mark, i)) continue
      const end = s.indexOf(mark, i + mark.length)
      if (end <= i) continue
      const inner = s.slice(i + mark.length, end)
      if (!inner) continue
      flush()
      out.push({ type, children: parseInline(inner) })
      i = end + mark.length
      continue outer
    }

    text += ch
    i += 1
  }

  flush()
  return out
}

function matchLink(s, start) {
  // [label](href) с учётом вложенных скобок в label.
  let depth = 0
  let i = start
  for (; i < s.length; i += 1) {
    if (s[i] === '\\') { i += 1; continue }
    if (s[i] === '[') depth += 1
    else if (s[i] === ']') {
      depth -= 1
      if (depth === 0) break
    }
  }
  if (depth !== 0 || s[i + 1] !== '(') return null
  const close = s.indexOf(')', i + 2)
  if (close < 0) return null
  return {
    label: s.slice(start + 1, i),
    href: s.slice(i + 2, close).trim(),
    end: close + 1,
  }
}

/** Ссылка безопасна для перехода: javascript:/data: не пропускаем. */
export function safeHref(href) {
  const value = String(href || '').trim()
  if (/^(https?:|mailto:|tel:|\/|#)/i.test(value)) return value
  return null
}
